import { FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@core/lib/utils";

export interface QuotaChipProps {
  used: number;
  /** Limit; use 0 for "not available", 999999 for "unlimited". */
  limit: number;
  onClick?: () => void;
  className?: string;
}

/**
 * Pill-style quota indicator for the dashboard header. Replaces the large
 * banner when `enableRedesignPollsV2` is on. Clicking opens the journal
 * (or any handler the parent provides).
 */
export function QuotaChip({ used, limit, onClick, className }: QuotaChipProps) {
  const { t } = useTranslation("polls");
  const isUnlimited = limit >= 999999;
  const isUnavailable = limit === 0;
  const isOverLimit = !isUnlimited && !isUnavailable && used >= limit;

  const label = isUnavailable
    ? t("quota_chip_unavailable")
    : isUnlimited
      ? t("quota_chip_unlimited")
      : t("quota_chip_count", { used, limit });

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors",
        "border",
        isOverLimit
          ? "bg-destructive/10 text-destructive border-destructive/30 hover:bg-destructive/15"
          : "bg-muted text-muted-foreground border-border hover:bg-muted/80",
        className,
      )}
      aria-label={t("quota_chip_aria", { label })}
      data-testid="quota-chip"
    >
      <FileText className="h-3 w-3" aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}
